import React, { useEffect, useState } from "react"
import _ from "lodash"

import { updateAirportSeasonsSelection } from "../containers/selectionSlice"

import { IMainFlightSelectorProps } from "./FlightSelector"
import SeasonSelector from "./SeasonSelector"
import AirportSelector from "./AirportSelector"
import { FieldName } from "../common/text"
import { formatDateShortwYear } from "./Utils"
import { useAppDispatch, useAppSelector } from "../app/hooks"

interface Props extends IMainFlightSelectorProps {
  codeType?: string
}

const AirportSeasonSelector: React.FunctionComponent<Props> = ({
  airportSeasons,
  handleChange,
  airportseasonSelectorConfig,
  codeType,
  hasError,
  errorMsg,
  touched,
  handleAvailChange,
  emptyMatrix
}) => {
  const dispatch = useAppDispatch()

  const { season, countries, airports, useLocalTime, useICAOCode } =
    useAppSelector((state) => state["selectionReducer"].airportSeasonsSelector)
  const [lastUpdated, setLastUpdated] = useState(null)

  useEffect(() => {
    if (
      useLocalTime !== undefined &&
      useICAOCode !== undefined &&
      airportSeasons.length &&
      season
    ) {
      const filterICAO = airportseasonSelectorConfig.selectCodeType
        ? useICAOCode // availability pages
        : codeType === "ICAO" // from non-availability page config

      const codeAirportSeasons = airportSeasons.filter((as) =>
        filterICAO ? as.supportsIcao : as.supportsIata
      )
      const timeAirportSeasons = airportSeasons.filter(
        (as) =>
          (useLocalTime ? as.supportsLt : as.supportsUtc) &&
          (filterICAO ? as.supportsIcao : as.supportsIata)
      )
      const seasonExists = codeAirportSeasons.find(
        (tas) => tas.season === season
      )
      const updAirports = airports.filter((a) =>
        timeAirportSeasons.find((tas) => tas.airport === a)
      )
      const updCountries = countries.filter((c) =>
        timeAirportSeasons.find((tas) => tas.country === c)
      )
      if (!seasonExists) {
        const uniqSeasons = _.uniqBy(timeAirportSeasons, (a) => a.season)
        if (uniqSeasons.length > 0) {
          dispatch(
            updateAirportSeasonsSelection({
              field: FieldName.season,
              value: uniqSeasons[0].season
            })
          )
        }
      }
      if (!_.isEqual(airports, updAirports)) {
        dispatch(
          updateAirportSeasonsSelection({
            field: FieldName.airports,
            value: updAirports
          })
        )
      }
      if (!_.isEqual(countries, updCountries)) {
        dispatch(
          updateAirportSeasonsSelection({
            field: FieldName.countries,
            value: updCountries
          })
        )
      }
    }
  }, [
    useLocalTime,
    useICAOCode,
    airportSeasons,
    season,
    airports,
    countries,
    codeType,
    airportseasonSelectorConfig.selectCodeType,
    dispatch
  ])

  const handleSetLastUpdated = () => {
    const _airport =
      airports.length > 0
        ? airports[0]
        : airportSeasons
            .filter(
              (as) =>
                as.season === season &&
                countries.indexOf(as.country) >= 0 &&
                (useLocalTime ? as.supportsLt : as.supportsUtc)
            )
            .map((a) => a.airport)[0]
    const airportSeason = airportSeasons.find(
      (as) => as.season === season && _airport === as.airport
    )
    setLastUpdated(
      airportSeason
        ? airportSeason.lastUpdated !== ""
          ? airportSeason.lastUpdated
          : null
        : null
    )
  }

  useEffect(() => {
    if (airports.length > 0 || countries.length > 0) {
      handleSetLastUpdated()
    }
    // eslint-disable-next-line
  }, [season, airports, countries])

  return (
    <div className={"d-flex flex-column " + (!lastUpdated ? "ocs-mb-12" : "")}>
      <div className="ocs-mb-12">
        <SeasonSelector
          airportSeasons={airportSeasons}
          supportsICAO={
            airportseasonSelectorConfig.selectCodeType
              ? useICAOCode
              : codeType === "ICAO"
          }
          emptyMatrix={emptyMatrix}
        />
      </div>
      <AirportSelector
        codeType={codeType}
        airportseasonSelectorConfig={airportseasonSelectorConfig}
        airportSeasons={airportSeasons}
        handleChange={handleChange}
        hasError={hasError}
        errorMsg={errorMsg}
        touched={touched}
        handleAvailChange={handleAvailChange}
      />
      {lastUpdated && (
        <p className="last-updated-info-txt">
          {"Last Updated: " + formatDateShortwYear(new Date(lastUpdated))}
        </p>
      )}
    </div>
  )
}

export default AirportSeasonSelector
